import Konva from 'konva/lib/index';
import QRCode from 'qrcode';

export const MOBILE_KONVA_RENDERER = 'everyid.mobile-konva/1';

const CARD_SIZES = {
  cr80: { width: 1012, height: 638 }
};

const SURFACES = {
  front: 'objectsFront',
  back: 'objectsBack',
  frontSilver: 'objectsFrontSilver',
  backSilver: 'objectsBackSilver'
};

const SUPPORTED_TYPES = new Set([
  'rect', 'line', 'polygon', 'circle', 'ellipse', 'arrow', 'star',
  'text', 'image', 'photo', 'qrcode'
]);

const number = (value, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

export const getTextOrigin = (object, padding, verticalOffset = 0) => ({
  x: number(object.x) + padding,
  y: number(object.y) + padding + verticalOffset,
  rotation: number(object.rotation)
});

const fieldValue = (fields, key) => {
  if (!key) return undefined;
  if (Object.prototype.hasOwnProperty.call(fields || {}, key)) return fields[key];
  const match = Object.keys(fields || {}).find(candidate =>
    candidate.toLowerCase() === String(key).toLowerCase());
  return match ? fields[match] : undefined;
};

export const replaceDynamicText = (value, fields = {}) =>
  String(value ?? '').replace(/\{([^{}]+)\}/g, (_, key) =>
    String(fieldValue(fields, key.trim()) ?? ''));

const conditionMatches = (actual, condition = {}) => {
  const left = String(actual ?? '');
  const right = String(condition.value ?? condition.role ?? '');
  switch (String(condition.operator || 'equals').toLowerCase()) {
    case 'notequals': case 'not-equals': case '!=': return left !== right;
    case 'contains': return left.includes(right);
    case 'startswith': case 'starts-with': return left.startsWith(right);
    case 'endswith': case 'ends-with': return left.endsWith(right);
    case 'greaterthan': case '>': return Number(left) > Number(right);
    case 'lessthan': case '<': return Number(left) < Number(right);
    default: return left === right;
  }
};

const applyOverrides = (object, fields) => {
  const binding = object.dataBinding;
  const actual = fieldValue(fields, binding?.fieldKey || binding?.fieldId);
  const condition = binding?.conditions?.find(item => conditionMatches(actual, item));
  if (!condition) return { ...object };
  const overrides = Array.isArray(condition.overrides)
    ? Object.fromEntries(condition.overrides.map(item => [item.property, item.value]))
    : (condition.overrides || {});
  return { ...object, ...overrides };
};

export function getCardDimensions(cardSettings = {}) {
  const base = CARD_SIZES[String(cardSettings.size || 'cr80').toLowerCase()];
  if (!base) return null;
  return String(cardSettings.format || 'landscape').toLowerCase() === 'portrait'
    ? { width: base.height, height: base.width }
    : { ...base };
}

const surfaceObjects = (cardDesign, surface) => {
  const key = SURFACES[surface] || SURFACES.front;
  const selected = cardDesign?.[key];
  if (Array.isArray(selected) && selected.length) return selected;
  return surface === 'front' && Array.isArray(cardDesign?.objects)
    ? cardDesign.objects
    : [];
};

export function assessCardDesign(cardDesign, { surface = 'front', requirePhoto = false } = {}) {
  const reasons = [];
  const settings = cardDesign?.cardSettings || {};
  const dimensions = getCardDimensions(settings);
  const objects = surfaceObjects(cardDesign, surface);

  if (!cardDesign) reasons.push('Card design is missing.');
  if (String(settings.size || 'cr80').toLowerCase() !== 'cr80') reasons.push('Only CR-80 is supported.');
  if (String(settings.sides || 'Simplex').toLowerCase() !== 'simplex') reasons.push('Duplex cards use the cloud renderer.');
  if (settings.silverFront === 'yes' || settings.silverBack === 'yes' || settings.silverPrint === 'yes') {
    reasons.push('Silver output uses the cloud renderer.');
  }
  if (surface !== 'front') reasons.push('The mobile R1 slice renders the front surface only.');
  if ((cardDesign?.objectsBack || []).length || (cardDesign?.objectsFrontSilver || []).length ||
      (cardDesign?.objectsBackSilver || []).length) reasons.push('Additional card surfaces use the cloud renderer.');
  if (!dimensions) reasons.push('Card dimensions are unsupported.');
  if (!objects.length) reasons.push('The front surface is empty.');

  const photos = objects.filter(object => object?.type === 'photo');
  if (requirePhoto && photos.length === 0) reasons.push('A capture photo frame is required.');
  if (photos.length > 2) reasons.push('More than two photo frames use the cloud renderer.');

  objects.forEach((object, index) => {
    if (!object || !SUPPORTED_TYPES.has(String(object.type || '').toLowerCase())) {
      reasons.push(`Object ${index + 1} has an unsupported type.`);
    }
    if (object?.containerId) reasons.push(`Object ${object.id || index + 1} uses a container transform.`);
    if (object?.type === 'image' && !(object.originalSrc || object.src)) {
      reasons.push(`Image ${object.id || index + 1} has no source.`);
    }
  });

  return {
    supported: reasons.length === 0,
    reasons: [...new Set(reasons)],
    width: dimensions?.width || 0,
    height: dimensions?.height || 0,
    objects
  };
}

export const resolvePhotoSource = (object, photoSource) =>
  photoSource || object?.originalSrc || object?.src;

const loadImage = source => new Promise((resolve, reject) => {
  if (!source) return reject(new Error('Image source is missing.'));
  const image = new window.Image();
  if (!/^(blob:|data:)/i.test(source)) image.crossOrigin = 'anonymous';
  image.decoding = 'async';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('A card image could not be loaded.'));
  image.src = source;
});

export const createRenderImageLoader = (loader = loadImage) => {
  const cache = new Map();
  return source => {
    if (!cache.has(source)) cache.set(source, loader(source));
    return cache.get(source);
  };
};

const common = object => ({
  x: number(object.x),
  y: number(object.y),
  rotation: number(object.rotation),
  opacity: object.opacity == null ? 1 : number(object.opacity, 1),
  shadowColor: object.shadowColor,
  shadowBlur: number(object.shadowBlur),
  shadowOffsetX: number(object.shadowOffsetX),
  shadowOffsetY: number(object.shadowOffsetY),
  shadowOpacity: object.shadowOpacity == null ? 1 : number(object.shadowOpacity, 1),
  listening: false,
  perfectDrawEnabled: true
});

const gradient = object => object.fillLinearGradientColorStops?.length >= 4 ? {
  fill: undefined,
  fillLinearGradientStartPoint: object.fillLinearGradientStartPoint,
  fillLinearGradientEndPoint: object.fillLinearGradientEndPoint,
  fillLinearGradientColorStops: object.fillLinearGradientColorStops
} : { fill: object.fill };

const resolveText = (object, fields) => {
  const binding = object.dataBinding;
  const bound = fieldValue(fields, binding?.fieldKey || binding?.fieldId || object.textBinding);
  return replaceDynamicText(bound == null || bound === '' ? object.text : bound, fields);
};

const fittedFontSize = (object, text, width, height) => {
  let low = 8;
  let high = Math.max(low, number(object.fontSize, 24));
  let best = low;
  const probe = new Konva.Text({
    text, width, fontFamily: object.fontFamily || 'Arial',
    fontStyle: object.fontStyle || 'normal', wrap: 'word',
    lineHeight: number(object.lineHeight, 1.2)
  });
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    probe.fontSize(middle);
    if (probe.height() <= height) { best = middle; low = middle + 1; }
    else high = middle - 1;
  }
  probe.destroy();
  return best;
};

export const getPhotoSourceCrop = (image, frameWidth, frameHeight, crop = {}) => {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const zoom = Math.max(1, number(crop?.position?.zoom ?? crop.scale, 1));
  const coverScale = Math.max(frameWidth / sourceWidth, frameHeight / sourceHeight) * zoom;
  const width = frameWidth / coverScale;
  const height = frameHeight / coverScale;
  const centerX = Number.isFinite(Number(crop?.position?.centerX))
    ? Number(crop.position.centerX) * sourceWidth
    : number(crop.x, (sourceWidth - width) / 2) + width / 2;
  const centerY = Number.isFinite(Number(crop?.position?.centerY))
    ? Number(crop.position.centerY) * sourceHeight
    : number(crop.y, (sourceHeight - height) / 2) + height / 2;
  return {
    x: Math.min(Math.max(0, centerX - width / 2), Math.max(0, sourceWidth - width)),
    y: Math.min(Math.max(0, centerY - height / 2), Math.max(0, sourceHeight - height)),
    width,
    height
  };
};

const imageCoverCrop = (image, width, height) => {
  const iw = image.naturalWidth || image.width;
  const ih = image.naturalHeight || image.height;
  const imageAspect = iw / ih;
  const boxAspect = width / height;
  if (imageAspect > boxAspect) {
    const cropWidth = ih * boxAspect;
    return { x: (iw - cropWidth) / 2, y: 0, width: cropWidth, height: ih };
  }
  const cropHeight = iw / boxAspect;
  return { x: 0, y: (ih - cropHeight) / 2, width: iw, height: cropHeight };
};

const addFilteredImage = (layer, object, image, crop) => {
  const filters = [
    number(object.brightness) !== 0 ? Konva.Filters.Brighten : null,
    number(object.contrast) !== 0 ? Konva.Filters.Contrast : null,
    number(object.grayscale) > 0 ? Konva.Filters.Grayscale : null,
    number(object.sepia) > 0 ? Konva.Filters.Sepia : null
  ].filter(Boolean);
  const node = new Konva.Image({
    ...common(object), image, width: number(object.width, image.width),
    height: number(object.height, image.height), crop,
    stroke: object.stroke, strokeWidth: number(object.strokeWidth),
    filters, brightness: number(object.brightness), contrast: number(object.contrast)
  });
  layer.add(node);
  if (filters.length) node.cache({ pixelRatio: 1 });
};

async function addObject(layer, input, context) {
  const object = applyOverrides(input, context.fields);
  if (object.visible === false) return;
  const type = String(object.type || '').toLowerCase();
  const base = { ...common(object), stroke: object.stroke, strokeWidth: number(object.strokeWidth), dash: object.dash || [] };

  if (type === 'rect') {
    layer.add(new Konva.Rect({ ...base, ...gradient(object), width: number(object.width),
      height: number(object.height), cornerRadius: number(object.cornerRadius) }));
    return;
  }
  if (['polygon', 'circle', 'ellipse', 'arrow', 'star'].includes(type)) {
    layer.add(new Konva.Line({ ...base, ...gradient(object), points: object.points || [], closed: true }));
    return;
  }
  if (type === 'line') {
    layer.add(new Konva.Line({ ...base, points: object.points || [0, 0, 120, 0],
      fill: undefined, lineCap: 'round' }));
    return;
  }
  if (type === 'text') {
    const text = resolveText(object, context.fields);
    const padding = number(object.padding, 6);
    const width = Math.max(1, number(object.width, 1) - padding * 2);
    const height = Math.max(1, number(object.height, 1) - padding * 2);
    const fontSize = String(object.textFrameMode || 'fixed').toLowerCase() === 'shrink'
      ? fittedFontSize(object, text, width, height)
      : number(object.fontSize, 24);
    const probe = new Konva.Text({ text, width, fontSize, fontFamily: object.fontFamily || 'Arial',
      fontStyle: object.fontStyle || 'normal', wrap: 'word', lineHeight: number(object.lineHeight, 1.2) });
    const available = String(object.textFrameMode || 'fixed').toLowerCase() === 'auto' ? probe.height() : height;
    let y = padding;
    if (available > probe.height() && object.verticalAlign === 'middle') y += (available - probe.height()) / 2;
    if (available > probe.height() && object.verticalAlign === 'bottom') y += available - probe.height();
    probe.destroy();
    layer.add(new Konva.Text({
      ...common(object), ...getTextOrigin(object, padding, y - padding), text,
      width, height: String(object.textFrameMode || 'fixed').toLowerCase() === 'auto' ? undefined : height,
      fontSize, fontFamily: object.fontFamily || 'Arial', fontStyle: object.fontStyle || 'normal',
      textDecoration: object.textDecoration, align: object.align || 'left',
      lineHeight: number(object.lineHeight, 1.2), fill: object.fill || '#000000', wrap: 'word',
      stroke: 'transparent', strokeWidth: 1, dash: object.dash, listening: false
    }));
    return;
  }
  if (type === 'image') {
    const image = await context.loadImage(object.originalSrc || object.src);
    const crop = object.fitMode === 'cover'
      ? imageCoverCrop(image, number(object.width, image.width), number(object.height, image.height))
      : undefined;
    addFilteredImage(layer, object, image, crop);
    return;
  }
  if (type === 'photo') {
    const source = resolvePhotoSource(object, context.photoSource);
    const image = await context.loadImage(source);
    const crop = getPhotoSourceCrop(image, number(object.width), number(object.height), context.crop);
    addFilteredImage(layer, object, image, crop);
    return;
  }
  if (type === 'qrcode') {
    const value = resolveText({ ...object, text: object.qrValue || object.text || ' ' }, context.fields) || ' ';
    const url = await QRCode.toDataURL(value, {
      margin: 0,
      color: { dark: object.qrColor || '#000000', light: '#00000000' },
      errorCorrectionLevel: 'Q'
    });
    const image = await context.loadImage(url);
    const quiet = Math.max(0, number(object.qrQuietZone));
    const group = new Konva.Group({ ...common(object) });
    group.add(new Konva.Rect({ width: number(object.width), height: number(object.height),
      fill: object.qrBackground || '#ffffff', listening: false }));
    group.add(new Konva.Image({ image, x: quiet, y: quiet,
      width: Math.max(1, number(object.width) - quiet * 2),
      height: Math.max(1, number(object.height) - quiet * 2), listening: false, imageSmoothingEnabled: false }));
    layer.add(group);
  }
}

const canvasBlob = canvas => new Promise((resolve, reject) => {
  if (typeof canvas.toBlob === 'function') {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('PNG export returned no data.')), 'image/png');
    return;
  }
  fetch(canvas.toDataURL('image/png')).then(response => response.blob()).then(resolve, reject);
});

export async function renderCardToBlob({
  cardDesign,
  surface = 'front',
  fields = {},
  photoSource,
  crop,
  loadFonts,
  requirePhoto = false
}) {
  const assessment = assessCardDesign(cardDesign, { surface, requirePhoto });
  if (!assessment.supported) {
    const error = new Error(assessment.reasons.join(' '));
    error.code = 'UNSUPPORTED_CARD_DESIGN';
    error.reasons = assessment.reasons;
    throw error;
  }

  if (loadFonts) await loadFonts();
  if (document.fonts?.ready) await document.fonts.ready;

  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  Object.assign(container.style, { position: 'fixed', left: '-100000px', top: '0',
    width: `${assessment.width}px`, height: `${assessment.height}px`, overflow: 'hidden' });
  document.body.appendChild(container);

  const stage = new Konva.Stage({ container, width: assessment.width, height: assessment.height });
  const layer = new Konva.Layer({ listening: false });
  const clip = new Konva.Group({ clipX: 0, clipY: 0, clipWidth: assessment.width,
    clipHeight: assessment.height, listening: false });
  layer.add(new Konva.Rect({ x: 0, y: 0, width: assessment.width, height: assessment.height,
    fill: '#ffffff', listening: false }));
  layer.add(clip);
  stage.add(layer);

  try {
    const renderImage = createRenderImageLoader();
    for (const object of assessment.objects) {
      await addObject(clip, object, { fields, photoSource, crop, loadImage: renderImage });
    }
    layer.draw();
    if (clip.getChildren().length !== assessment.objects.filter(object => object?.visible !== false).length) {
      throw new Error('The card scene did not finish loading every object.');
    }
    const canvas = stage.toCanvas({ x: 0, y: 0, width: assessment.width, height: assessment.height, pixelRatio: 1 });
    if (canvas.width !== assessment.width || canvas.height !== assessment.height) {
      throw new Error('The exported card dimensions are incorrect.');
    }
    const blob = await canvasBlob(canvas);
    if (!blob || blob.type !== 'image/png' || blob.size < 512) {
      throw new Error('The exported card PNG is blank or incomplete.');
    }
    return { blob, width: assessment.width, height: assessment.height,
      mimeType: 'image/png', objectCount: assessment.objects.length, renderer: MOBILE_KONVA_RENDERER };
  } finally {
    stage.destroy();
    container.remove();
  }
}

